import { useState, useEffect } from 'react'
import { Search, Edit2, Eye, RefreshCw, ChevronLeft, ChevronRight, Filter, Trash2 } from 'lucide-react'
import { adminAPI } from '../services/api'
import UserModal from '../components/UserModal.js'

const Users = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [modalMode, setModalMode] = useState('view') // 'view', 'edit'
  
  // Пагинация
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasNext: false,
    hasPrev: false
  })
  
  // Фильтры (сортировка всегда по ID DESC)
  const [filters, setFilters] = useState({
    region: '',
    status: '',
    sync: ''
  })
  
  // Кэш для результатов поиска
  const [searchCache, setSearchCache] = useState(new Map())
  
  // Состояние UI
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    fetchUsers()
  }, [pagination.currentPage, filters, searchTerm])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      
      // Создаем ключ для кэша
      const cacheKey = JSON.stringify({
        page: pagination.currentPage,
        limit: pagination.limit,
        search: searchTerm,
        ...filters
      })
      
      // Проверяем кэш
      if (searchCache.has(cacheKey)) {
        const cachedData = searchCache.get(cacheKey)
        setUsers(cachedData.users)
        setPagination(cachedData.pagination)
        setLoading(false)
        return
      }
      
      // Параметры запроса - всегда сортируем по ID DESC
      const params = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.limit.toString(),
        search: searchTerm,
        region: filters.region,
        status: filters.status,
        sortBy: 'id', // Принудительно сортируем по ID
        sortOrder: 'DESC' // Принудительно от большего к меньшему
      })
      
      const response = await adminAPI.getUsers(params.toString())
      
      if (response.data.success) {
        const data = response.data
        
        // Принудительно сортируем по ID DESC на клиенте
        const sortedUsers = [...data.users].sort((a, b) => {
          const idA = parseInt(a.id)
          const idB = parseInt(b.id)
          return idB - idA // DESC - от большего к меньшему
        })
        
        setUsers(sortedUsers)
        setPagination(data.pagination)
        
        // Кэшируем результат с отсортированными данными
        setSearchCache(prev => {
          const newCache = new Map(prev)
          newCache.set(cacheKey, { ...data, users: sortedUsers })
          // Ограничиваем размер кэша
          if (newCache.size > 50) {
            const firstKey = newCache.keys().next().value
            newCache.delete(firstKey)
          }
          return newCache
        })
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleEditUser = (user) => {
    setSelectedUser(user)
    setModalMode('edit')
    setShowModal(true)
  }

  const handleViewUser = (user) => {
    setSelectedUser(user)
    setModalMode('view')
    setShowModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    setSelectedUser(null)
  }

  const handleUserSaved = (updatedUser) => {
    // Немедленно обновляем пользователя в локальном списке для мгновенной обратной связи
    if (updatedUser) {
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === updatedUser.id ? { ...user, ...updatedUser } : user
        )
      )
    }
    
    // Очищаем кэш при обновлении пользователя
    setSearchCache(new Map())
    handleModalClose()
  }

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Ви впевнені, що хочете видалити користувача "${user.full_name}"?`)) {
      try {
        await adminAPI.deleteUser(user.id)
        
        // Немедленно удаляем пользователя из локального списка для мгновенной обратной связи
        setUsers(prevUsers => prevUsers.filter(u => u.id !== user.id))
        
        // Если удаляем последнего пользователя на странице, переходим на предыдущую
        if (users.length === 1 && pagination.currentPage > 1) {
          setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))
        }
        
        // Очищаем кэш при удалении пользователя
        setSearchCache(new Map())
        
        // НЕ вызываем fetchUsers() - полагаемся на локальное состояние
        // Данные обновятся при следующем переходе страницы или фильтрации
        
      } catch (error) {
        console.error('Error deleting user:', error)
        alert('Помилка при видаленні користувача: ' + (error.response?.data?.error || error.message))
        // В случае ошибки перезагружаем данные для восстановления состояния
        fetchUsers()
      }
    }
  }

  // Обработчики пагинации
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }))
  }

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ 
      ...prev, 
      limit: newLimit, 
      currentPage: 1 
    }))
  }

  // Обработчики фильтров
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }))
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  // Сортировка всегда по ID DESC - функция удалена

  const clearFilters = () => {
    setFilters({
      region: '',
      status: '',
      sync: ''
    })
    setSearchTerm('')
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  // Подсчет активных фильтров
  const getActiveFiltersCount = () => {
    let count = 0
    if (filters.region) count++
    if (filters.status) count++
    if (filters.sync) count++
    return count
  }

  // Быстрые фильтры-чипсы
  const quickFilter = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: prev[filterType] === value ? '' : value }))
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  // Debounced search
  const [searchTimeout, setSearchTimeout] = useState(null)
  const handleSearchChange = (value) => {
    setSearchTerm(value)
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    
    // Очищаем предыдущий таймаут
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    
    // Устанавливаем новый таймаут для поиска
    const timeout = setTimeout(() => {
      // Поиск будет выполнен в useEffect
    }, 300)
    setSearchTimeout(timeout)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const getSyncStatusBadge = (sync) => {
    return sync ? 'badge badge-success' : 'badge badge-warning'
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return 'badge badge-success'
      case 'inactive':
        return 'badge badge-error'
      case 'blocked':
        return 'badge badge-info'
      default:
        return 'badge badge-info'
    }
  }

  const formatBalance = (balance) => {
    if (!balance) return '0 грн'
    return `${parseInt(balance).toLocaleString()} грн`
  }

  const formatDiscount = (discount) => {
    if (!discount) return '0%'
    return `${discount}%`
  }


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Користувачі</h1>
          <p className="text-gray-600">Управління користувачами системи</p>
        </div>
        <button
          onClick={fetchUsers}
          disabled={loading}
          className="btn btn-secondary flex items-center space-x-2"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          <span>Оновити</span>
        </button>
      </div>

      <div className="card p-6">
        {/* Поиск и фильтры */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-4 lg:space-y-0 lg:space-x-4 mb-6">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Пошук за ім'ям, телефоном, email, номером картки, регіоном або статусом..."
              className="input pl-10"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          
          <div className="flex items-center space-x-2 w-full lg:w-auto">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn btn-secondary flex items-center space-x-2 ${showFilters ? 'bg-gray-200' : ''}`}
            >
              <Filter size={20} />
              <span className="hidden sm:inline">Фільтри</span>
            </button>
            
            <button
              onClick={clearFilters}
              className="btn btn-secondary"
            >
              <span className="hidden sm:inline">Очистити</span>
              <span className="sm:hidden">×</span>
            </button>
          </div>
        </div>

        {/* Панель фильтров */}
        {showFilters && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Регіон
                </label>
                <select
                  value={filters.region}
                  onChange={(e) => handleFilterChange('region', e.target.value)}
                  className="input"
                >
                  <option value="">Всі регіони</option>
                  <option value="Київ">Київ</option>
                  <option value="Харків">Харків</option>
                  <option value="Одеса">Одеса</option>
                  <option value="Дніпро">Дніпро</option>
                  <option value="Львів">Львів</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Статус
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input"
                >
                  <option value="">Всі статуси</option>
                  <option value="active">Активний</option>
                  <option value="blocked">Заблокований</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Сортування
                </label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="input"
                >
                  <option value="created_at">Дата створення</option>
                  <option value="full_name">Ім'я</option>
                  <option value="phone">Телефон</option>
                  <option value="card_number">Номер картки</option>
                  <option value="balance">Баланс</option>
                  <option value="discount_percent">Знижка</option>
                </select>
              </div>
              
              {/* Сортировка всегда по ID DESC - кнопка удалена */}
            </div>
          </div>
        )}

        {/* Информация о результатах */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-2 sm:space-y-0">
          <div className="text-sm text-gray-600">
            Показано {users.length} з {pagination.totalCount} користувачів
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Записів на сторінку:</span>
            <select
              value={pagination.limit}
              onChange={(e) => handleLimitChange(parseInt(e.target.value))}
              className="input w-20 text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="table min-w-full">
                <thead>
                  <tr>
                    <th className="hidden md:table-cell px-3 py-2 text-xs">ID</th>
                    <th className="px-3 py-2 text-xs">Ім'я</th>
                    <th className="hidden lg:table-cell px-3 py-2 text-xs">Телефон</th>
                    <th className="hidden xl:table-cell px-3 py-2 text-xs">Email</th>
                    <th className="hidden lg:table-cell px-3 py-2 text-xs">Картка</th>
                    <th className="hidden md:table-cell px-3 py-2 text-xs">Регіон</th>
                    <th className="hidden xl:table-cell px-3 py-2 text-xs">Дата народження</th>
                    <th className="hidden xl:table-cell px-3 py-2 text-xs">Баланс</th>
                    <th className="hidden xl:table-cell px-3 py-2 text-xs">Знижка</th>
                    <th className="hidden md:table-cell px-3 py-2 text-xs">Статус</th>
                    <th className="px-2 py-2 text-xs text-center" title="Синхронізація">●</th>
                    <th className="px-3 py-2 text-xs">Дії</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="font-medium hidden md:table-cell px-3 py-3 text-sm">{user.id}</td>
                      <td className="px-3 py-3">
                        <div>
                          <div className="font-semibold text-sm">{user.full_name || '-'}</div>
                          <div className="text-xs text-gray-500 md:hidden mt-1">
                            {user.phone || '-'} • {user.region || '-'}
                          </div>
                        </div>
                      </td>
                      <td className="hidden lg:table-cell px-3 py-3 text-xs">{user.phone || '-'}</td>
                      <td className="hidden xl:table-cell px-3 py-3 text-xs">{user.email || '-'}</td>
                      <td className="hidden lg:table-cell px-3 py-3 font-mono text-xs">{user.card_number || '-'}</td>
                      <td className="hidden md:table-cell px-3 py-3 text-xs">{user.region || '-'}</td>
                      <td className="hidden xl:table-cell px-3 py-3 text-xs">{formatDate(user.birth_date)}</td>
                      <td className="hidden xl:table-cell px-3 py-3 font-medium text-green-600 text-xs">{formatBalance(user.balance)}</td>
                      <td className="hidden xl:table-cell px-3 py-3 font-medium text-blue-600 text-xs">{formatDiscount(user.discount_percent)}</td>
                      <td className="hidden md:table-cell px-3 py-3">
                        <span className={getStatusBadge(user.status)}>
                          {user.status === 'active' ? 'Активний' : 
                           user.status === 'blocked' ? 'Заблокований' : 'Невідомий'}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-center">
                        <div className="flex justify-center items-center" title={user.sync ? 'Синхронізовано' : 'Не синхронізовано'}>
                          <span className={`inline-block w-2 h-2 rounded-full ${user.sync ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Перегляд"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Редагувати"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Видалити"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {users.length === 0 && !loading && (
              <div className="text-center py-8">
                <p className="text-gray-500">Користувачі не знайдені</p>
              </div>
            )}
          </>
        )}

        {/* Пагинация */}
        {!loading && pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-6 border-t border-gray-200 space-y-4 sm:space-y-0">
            <div className="text-sm text-gray-600">
              Сторінка {pagination.currentPage} з {pagination.totalPages}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="btn btn-secondary flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed text-sm px-3 py-1"
              >
                <ChevronLeft size={14} />
                <span className="hidden sm:inline">Попередня</span>
              </button>
              
              {/* Номера страниц */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1
                  } else if (pagination.currentPage <= 3) {
                    pageNum = i + 1
                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i
                  } else {
                    pageNum = pagination.currentPage - 2 + i
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                        pageNum === pagination.currentPage
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="btn btn-secondary flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed text-sm px-3 py-1"
              >
                <span className="hidden sm:inline">Наступна</span>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <UserModal
          user={selectedUser}
          mode={modalMode}
          onClose={handleModalClose}
          onSave={handleUserSaved}
        />
      )}
    </div>
  )
}

export default Users
